import { useEffect, useRef, useState } from 'react';
import {
  Alert, Link, Box, Button, Chip, CircularProgress, Divider, IconButton, InputAdornment,
  LinearProgress, MenuItem, Paper,
  Select, Stack, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';

import { CoveragePanel } from './CoveragePanel';
import { TurnIcon } from './TurnIcon';
import { shapeOf, turnDistance } from '../util/turnShape';

import type {
  City, DirectionsLeg, RouteAlternative, RoutePlan, TravelMode,
} from '../api/types';
import { formatClock } from '../util/time';
import { isPassedStop, passedStopsCrowd, stopNumbering } from '../util/passedStops';
import { MODE_COLOURS, STOP_COLOURS, AVOID_VIOLATED_COLOUR } from '../theme';
import { MODE_GERUNDS, MODE_LABELS, SELECTABLE_TRAVEL_MODES } from '../api/types';

/**
 * Which stop a leg leaves, as the planner keys its overrides.
 *
 * Keyed by the place rather than by leg number so an override survives a
 * re-plan that reorders the day. A stop with no id of its own — a dropped pin
 * or a break — falls back to its position, which is the best that can be said
 * about something that has no other name.
 */
export function legModeKey(
  stops: RoutePlan['stops'],
  legIndex: number,
): string {
  if (legIndex === 0) return 'start';
  return stops[legIndex]?.poiId ?? `at:${legIndex}`;
}

function formatDistance(meters: number, units: City['units']): string {
  /*
    Miles or kilometres, never feet or metres.

    It used to drop to the small unit below a fifth of a mile, which reads
    well on its own and badly in a column: a schedule running "0.9 mi, 374 ft,
    1.2 mi" asks the reader to convert before two rows can be compared. A
    short distance takes a second decimal instead. See `turnDistance`, which
    this now simply is.
  */
  return turnDistance(meters, units);
}

/*
 * One step of the distance display, in metres.
 *
 * formatDistance shows one decimal, so two figures a tenth apart are the
 * smallest visible difference. Rounding to this before subtracting is what
 * makes a delta agree with the two numbers it sits between.
 */
function displayStep(units: City['units']): number {
  return units === 'imperial' ? 1609.344 / 10 : 100;
}

/*
 * A difference between two distances, in the unit they are shown in.
 *
 * Not formatDistance: that switches to feet or metres below a fifth of a
 * mile, which is right for a distance and wrong for a gap between two
 * figures printed in miles — "11.1 mi, 11.4 mi (528 ft)" makes the reader do
 * the conversion the caption exists to save them.
 */
function formatDistanceDelta(meters: number, units: City['units']): string {
  return units === 'imperial'
    ? `${(meters / 1609.344).toFixed(1)} mi`
    : `${(meters / 1000).toFixed(1)} km`;
}

/*
 * Whether the round goes past this one rather than stopping at it: the map
 * asks the same question of the same stops, so both ask it in one place.
 */
const passingBy = isPassedStop;

/** A row of the schedule, with any passed places folded into the gap. */
interface ScheduleRow {
  stop: RoutePlan['stops'][number];
  /** Its place in the plan, which a click, a focus and a reorder go by. */
  index: number;
  /** What it is called on the badge, counting only the rows shown. */
  number: number;
  /** The leg arriving at it, or nothing for the first row. */
  leg: RoutePlan['legs'][number] | undefined;
  /** Passed places folded away just before it, and the travel through them. */
  folded: number;
  foldedMinutes: number;
  foldedMetres: number;
}

/**
 * The schedule with the passed places folded away.
 *
 * Two hundred rows that each say "passes by, no stop" are not a schedule
 * anybody reads, so when the map puts them down the list does too. The
 * driving through them is not lost: it is added to the gap it belongs to, so
 * the times between the rows that remain still account for the whole day.
 */
function scheduleRows(plan: RoutePlan, fold: boolean): ScheduleRow[] {
  const numbers = stopNumbering(plan, fold);
  const rows: ScheduleRow[] = [];
  let folded = 0;
  let foldedMinutes = 0;
  let foldedMetres = 0;

  plan.stops.forEach((stop, index) => {
    const leg = plan.legs[index - 1];

    if (fold && passingBy(stop)) {
      folded += 1;
      foldedMinutes += leg?.durationMinutes ?? 0;
      foldedMetres += leg?.distanceMeters ?? 0;
      return;
    }

    rows.push({
      stop, index, number: numbers.get(index) ?? index,
      leg, folded, foldedMinutes, foldedMetres,
    });
    folded = 0;
    foldedMinutes = 0;
    foldedMetres = 0;
  });

  return rows;
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m} min`;
  // "5 hr", not "5 hr 0 min".
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

interface Props {
  /** Opens the place panel for a stop. Omit to keep names plain. */
  onShowDetails?: (poiId: string) => void;
  /**
   * Opens what is known about a stop that is a coordinate rather than a
   * place. Omit to leave pins as plain text.
   */
  onShowPoint?: (index: number) => void;
  plan: RoutePlan;
  city: City;
  onStopHover?: (index: number | null) => void;
  /**
   * A row was clicked: bring that stop into view on the map.
   *
   * Separate from hover, which already highlights. Hovering a list is how
   * somebody reads it, and a map that chased the cursor would be unusable.
   */
  onStopFocus?: (index: number) => void;
  /**
   * A stop picked on the MAP, to be scrolled to here.
   *
   * Carries a nonce for the same reason the map's does, and is separate from
   * `highlightedStopIndex` because that also changes on hover — scrolling the
   * list under the cursor somebody is reading with would be its own bug.
   */
  revealStop?: { index: number; nonce: number } | null;
  /**
   * Takes the map to a stop WITHOUT opening anything.
   *
   * The coverage tab's stop numbers use it. Absent, they fall back to
   * `onStopFocus`, which on some screens also opens the place panel.
   */
  onStopReveal?: (index: number) => void;
  /**
   * The tile archive this page's map draws.
   *
   * Passed through to the coverage tab, where opening a place opens a map of
   * its own — and a map with no archive behind it is a marker on an empty
   * background. The page already has the URL for its own map; working it out
   * a second time down here would mean reading the settings twice.
   */
  basemapUrl?: string | null;
  /**
   * Whether the places the round only passes are put down on the map.
   *
   * The schedule follows it: a round that passes two hundred places is two
   * hundred rows saying the same thing, and somebody who has hidden them to
   * see the line does not want to scroll past them here either. Only takes
   * effect where there are enough of them to be in the way — a round with
   * three keeps all three, whatever the map is doing.
   */
  passedHidden?: boolean;
  highlightedStopIndex?: number | null;
  /**
   * Fired when a stop is dragged to a new position, with indices into
   * `plan.stops`. Absent means the itinerary is not reorderable.
   */
  onReorder?: (fromIndex: number, toIndex: number) => void;
  /**
   * Fired when the stay at a stop is changed, in minutes. Absent means the
   * timings are fixed.
   */
  onDwellChange?: (index: number, minutes: number) => void;
  /** Switches the route to one of the offered alternatives. */
  onChooseAlternative?: (index: number) => void;
  /** Highlights an alternative on the map while it is hovered. */
  onHoverAlternative?: (index: number | null) => void;
  /** The day's budget, so the plan can say how much of it is used. */
  availableMinutes?: number;
  /** Drops a stop from the route. Absent means stops cannot be removed. */
  onRemoveStop?: (index: number) => void;
  /**
   * Stops taken off the request since this round was planned.
   *
   * They are still ON the drawn route, because the route has not been planned
   * again — so the row stays, struck through, rather than vanishing from a
   * line that still goes through it. Removing it outright would leave the
   * schedule describing a journey the map does not draw.
   */
  pendingRemoval?: number[];
  /**
   * How each leg is travelled, keyed by the stop it leaves.
   *
   * Absent means the whole day is travelled in the plan's own mode and no
   * picker is offered — which is what the trip screen wants, where a day's
   * mode is set on its card.
   */
  legModes?: Record<string, TravelMode>;
  /**
   * Every way round this journey and which is drawn, in a fixed order.
   *
   * Passed in rather than derived from `plan`, because the drawn one changes
   * as they are switched between and the LIST must not: a row that changes
   * what it holds when another is clicked reads as the list reordering.
   */
  waysRound?: { list: RouteAlternative[]; current: number };
  onLegModeChange?: (key: string, mode: TravelMode) => void;
  /**
   * Fetches the turn-by-turn for this plan, on request.
   *
   * Asked for rather than always shown: a round of twelve stops is three
   * hundred instructions, and somebody reading an itinerary wants to know
   * where they are going, not which exit of every roundabout. The call costs
   * a router request, so it is made once, the first time it is asked for.
   *
   * Absent means no offer is made at all.
   */
  directionsFor?: () => Promise<DirectionsLeg[]>;
  /**
   * The saved route or operation being looked at, left out of its own
   * coverage. See CoveragePanel; the planner has nothing to pass.
   */
  coverageIgnoreId?: string;
  /**
   * Which day this schedule is, where the screen holds several.
   *
   * The coverage tab analyses the round beside it, which on an operation is
   * one day; this is how it says so.
   */
  coverageDayNote?: string;
  /**
   * The date this round is planned for, YYYY-MM-DD.
   *
   * The coverage tab measures "before" against it, so that work planned for
   * a later date is not reported as ground already covered.
   */
  coverageRoundDate?: string;
}

export function ItineraryPanel({
  onShowDetails,
  onShowPoint,
  plan, city, onStopHover, onStopFocus, revealStop, highlightedStopIndex, onReorder, onDwellChange,
  onChooseAlternative, onHoverAlternative, availableMinutes, onRemoveStop,
  pendingRemoval = [],
  legModes = {}, onLegModeChange, waysRound, directionsFor, coverageIgnoreId,
  coverageDayNote, coverageRoundDate, onStopReveal, passedHidden, basemapUrl,
}: Props) {
  /*
   * The turns, once somebody asks for them.
   *
   * Held here rather than fetched by each leg: one call answers the whole
   * round, and a dozen legs asking separately would be a dozen requests for
   * the same reply. Cleared when the plan changes, because the instructions
   * are for the line that was drawn and not for the next one.
   */
  const [turns, setTurns] = useState<DirectionsLeg[] | null>(null);
  const [turnsShown, setTurnsShown] = useState(false);
  const [turnsLoading, setTurnsLoading] = useState(false);

  useEffect(() => {
    setTurns(null);
    setTurnsShown(false);
  }, [plan]);

  const toggleTurns = async () => {
    if (turnsShown) {
      setTurnsShown(false);

      return;
    }
    setTurnsShown(true);
    if (turns || !directionsFor) return;
    setTurnsLoading(true);
    try {
      setTurns(await directionsFor());
    } catch {
      // Nothing to show, and the row says so rather than spinning for ever.
      setTurns([]);
    } finally {
      setTurnsLoading(false);
    }
  };
  const { totals, infrastructureProximity: ips } = plan;

  /*
   * How this journey is travelled, in the words of the sentence it sits in.
   *
   * This was the literal string "driving" in every row, so a walking route
   * announced itself as a drive. Taken from the first leg rather than the
   * request: alternatives exist only for a direct route, so there is exactly
   * one leg and it is the one actually travelled.
   */
  const wayOfTravelling = MODE_GERUNDS[plan.legs[0]?.mode ?? 'car'];

  /** The avoided areas the route could not keep clear of. */
  const violatedZones = plan.avoidedZones.filter((zone) => zone.violated);


  /*
   * Native drag and drop rather than a library: this is one short vertical
   * list, and the whole gesture is three handlers.
   *
   * Only the stops between start and end can move — dragging the start of the
   * journey into the middle of it has no meaning.
   */
  const [dragFrom, setDragFrom] = useState<number | null>(null);

  /*
    The rows, so one picked on the map can be scrolled to.

    A click on a marker highlighted the matching row and opened its details,
    which is no help at all when the row is forty stops down a list nobody
    has scrolled. The map and the list are two views of one round, and
    picking a stop in either should show it in both.
  */
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!revealStop) return;

    rowRefs.current[revealStop.index]?.scrollIntoView({
      // Centred rather than nudged to an edge: a stop at the very top of the
      // panel reads as the start of the round rather than the one just
      // picked.
      behavior: 'smooth', block: 'center',
    });
    // The nonce is what makes picking the same stop again scroll back to it.
  }, [revealStop?.nonce, revealStop?.index]);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const movable = (index: number) =>
    Boolean(onReorder) && index > 0 && index < plan.stops.length - 1;

  /*
   * Which of the two the column is showing.
   *
   * Not lifted to the page: this is where somebody looks, not something the
   * page acts on, and all three pages that show an itinerary get the second
   * view by having this one. Reset with the plan, because a re-plan is a new
   * answer and the schedule is what was asked for.
   */
  const [view, setView] = useState<'schedule' | 'coverage'>('schedule');
  useEffect(() => { setView('schedule'); }, [plan]);

  /* Built on the first visit, and kept from then on. */
  const coverageOpened = useRef(false);
  if (view === 'coverage') coverageOpened.current = true;

  const schedule = (
    <Stack spacing={2}>
      {/* ── totals ───────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Metric label="Total" value={formatDuration(totals.totalMinutes)} />
          <Metric label="Travel" value={formatDuration(totals.travelMinutes)} />
          <Metric label="At stops" value={formatDuration(totals.dwellMinutes)} />
          <Metric label="Distance" value={formatDistance(totals.distanceMeters, city.units)} />
          <Metric
            label="Stops"
            value={String(Math.max(0, plan.stops.length - 2))}
          />
        </Stack>

        {/*
          How much of the day this actually uses. Without it, a plan that
          fills two hours of an available five looks like the whole answer,
          and there is nothing to suggest asking for more.
        */}
        {availableMinutes != null && availableMinutes > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (totals.totalMinutes / availableMinutes) * 100)}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatDuration(totals.totalMinutes)} of {formatDuration(availableMinutes)}
                {totals.totalMinutes < availableMinutes
                  && ` · ${formatDuration(availableMinutes - totals.totalMinutes)} spare`}
              </Typography>
            </Stack>
          </>
        )}
      </Paper>

      {/*
        Every way round, in one list that does not move.

        There was a "Current" row built from the plan and the alternatives
        beneath it, so choosing one moved its numbers up and pushed the drawn
        one down into the slot it had left. The rows stayed put and their
        contents traded places, which reads as the list reordering under the
        cursor. One list, fixed order, and only the badge moves.
      */}
      {onChooseAlternative && waysRound && waysRound.list.length > 1 && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Other ways round — click to switch
          </Typography>
          <Stack spacing={0.5}>
            {waysRound.list.map((way, at) => {
              const isCurrent = at === waysRound.current;
              const drawn = waysRound.list[waysRound.current];

              /*
               * Compared as SHOWN, not as held.
               *
               * Both times are rendered to the whole minute, but the totals
               * carry the seconds they were summed from, so subtracting the
               * raw values printed "+0.783333 min" beside a whole number.
               * Rounding each side first makes the caption agree with the two
               * figures it sits between, and turns a sub-minute gap into
               * "same time".
               */
              const deltaMin = Math.round(way.durationMinutes) - Math.round(drawn?.durationMinutes ?? 0);
              const step = displayStep(city.units);
              const shown = (m: number) => Math.round(m / step) * step;
              const deltaM = shown(way.distanceMeters) - shown(drawn?.distanceMeters ?? 0);

              return (
                <Stack
                  key={at}
                  direction="row"
                  spacing={1}
                  onClick={() => { if (!isCurrent) onChooseAlternative(at); }}
                  onMouseEnter={() => { if (!isCurrent) onHoverAlternative?.(at); }}
                  onMouseLeave={() => onHoverAlternative?.(null)}
                  sx={{
                    alignItems: 'center',
                    px: 1, py: 0.75, borderRadius: 1,
                    cursor: isCurrent ? 'default' : 'pointer',
                    bgcolor: isCurrent ? 'action.hover' : undefined,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Chip
                    size="small"
                    label={isCurrent ? 'Current' : `Way ${at + 1}`}
                    color={isCurrent ? 'primary' : undefined}
                    variant={isCurrent ? 'filled' : 'outlined'}
                    sx={{ height: 20 }}
                  />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {formatDuration(way.durationMinutes)} {wayOfTravelling}
                    {/* The comparison is the useful part, and the drawn one
                        has nothing to compare itself against. */}
                    {!isCurrent && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 0.75, color: deltaMin > 0 ? 'warning.main' : 'success.main' }}
                      >
                        {deltaMin === 0 ? 'same time' : `${deltaMin > 0 ? '+' : ''}${deltaMin} min`}
                      </Typography>
                    )}
                    {way.roadAnswer && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {way.roadAnswer}
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDistance(way.distanceMeters, city.units)}
                    {!isCurrent && deltaM !== 0 && (
                      ` (${deltaM > 0 ? '+' : ''}${formatDistanceDelta(Math.abs(deltaM), city.units)})`
                    )}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Paper>
      )}

      {plan.warnings.map((warning, i) => (
        <Alert key={i} severity="warning" variant="outlined">{warning}</Alert>
      ))}

      {/* ── itinerary ────────────────────────────────────────────── */}
      <Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}
        >
          <Typography variant="h3" gutterBottom>Mission schedule</Typography>
          {/* Offered, not imposed: see `directionsFor`. */}
          {directionsFor && (
            <Button
              size="small"
              onClick={() => void toggleTurns()}
              startIcon={turnsLoading
                ? <CircularProgress size={14} color="inherit" />
                : undefined}
              sx={{ textTransform: 'none' }}
            >
              {turnsShown ? 'Hide directions' : 'Turn-by-turn'}
            </Button>
          )}
        </Stack>
        <Stack>
          {scheduleRows(plan, Boolean(passedHidden) && passedStopsCrowd(plan))
            .map(({ stop, index, number, leg, folded, foldedMinutes, foldedMetres }) => {
            const highlighted = highlightedStopIndex === index;

            return (
              <Box key={`${stop.kind}-${index}`}>
                {leg && (
                  <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{ alignItems: 'center', pl: '11px', py: 0.75 }}
                  >
                    <Box
                      sx={{
                        width: 4, alignSelf: 'stretch', minHeight: 26, borderRadius: 2,
                        bgcolor: MODE_COLOURS[leg.mode],
                      }}
                    />
                    {/*
                      The gap between two rows, whole.

                      With the passed places folded away this is the drive
                      through all of them rather than one leg of it, so what
                      is written is the sum: the times down the list still
                      add up to the day, and the count says what was passed
                      on the way.
                    */}
                    <Typography variant="caption" color="text.secondary">
                      Travel {formatDuration(leg.durationMinutes + foldedMinutes)} ·{' '}
                      {formatDistance(leg.distanceMeters + foldedMetres, city.units)}
                      {folded > 0
                        ? ` · past ${folded} ${folded === 1 ? 'place' : 'places'}`
                        : onLegModeChange
                          ? ''
                          : ` by ${MODE_LABELS[leg.mode].toLowerCase()}`}
                    </Typography>
                    {/*
                      How this leg is travelled, changed in place.

                      On the leg rather than on the stop card because that is
                      what it describes: the walk between two places, not the
                      places. The picker sits where the mode is already
                      written, so nothing new appears on the screen — the
                      words simply became a control.
                    */}
                    {onLegModeChange && folded === 0 && (
                      <Select
                        size="small"
                        variant="standard"
                        disableUnderline
                        value={leg.mode}
                        onChange={(event) =>
                          onLegModeChange(
                            legModeKey(plan.stops, index - 1),
                            event.target.value as TravelMode,
                          )}
                        renderValue={(value) => (
                          <Typography variant="caption" color="text.secondary">
                            by {MODE_LABELS[value as TravelMode].toLowerCase()}
                          </Typography>
                        )}
                        sx={{
                          '& .MuiSelect-select': { py: 0, pr: '18px !important' },
                          '& .MuiSelect-icon': { fontSize: 16 },
                        }}
                      >
                        {SELECTABLE_TRAVEL_MODES.map((m) => (
                          <MenuItem key={m} value={m}>
                            {MODE_LABELS[m]}
                            {legModes[legModeKey(plan.stops, index - 1)] === m && ' ·'}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </Stack>
                )}

                {/*
                  The turns for this leg, where they were asked for.

                  Indented under the leg's own colour bar rather than given a
                  card: they belong to the travel between two stops, and a
                  boxed list of them would read as a third kind of thing in a
                  schedule that has two.
                */}
                {leg && turnsShown && (() => {
                  /*
                    Every leg of the gap, not just the last one.

                    A folded gap is the drive through all the places put down
                    — an hour and three quarters, past a hundred and three of
                    them. Read from the final leg alone it was five turns
                    ending "your destination is on the left", which is
                    directions for the last four hundred metres of it and
                    wrong for everything before.

                    Joined, the starts and arrivals in the middle go: they
                    mark places this round does not stop at, and "you have
                    arrived" a hundred times over is not what the driver is
                    being told. The first leg keeps its start and the last
                    keeps its arrival, so the run reads as the one drive it
                    is.
                  */
                  const start = index - 1 - folded;
                  const run = (turns ?? [])
                    .filter((d) => d.fromIndex >= start && d.fromIndex <= index - 1)
                    .sort((a, b) => a.fromIndex - b.fromIndex);
                  const steps = run.flatMap((d, at) => d.maneuvers.filter((m) => {
                    const shape = shapeOf(m.type);
                    if (shape === 'start') return at === 0;
                    if (shape === 'finish') return at === run.length - 1;

                    return true;
                  }));
                  if (turns === null) return null;
                  if (steps.length === 0) {
                    return (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', pl: 4, pb: 0.75, fontStyle: 'italic' }}
                      >
                        No turn-by-turn for this leg.
                      </Typography>
                    );
                  }

                  return (
                    <Stack component="ol" spacing={0.25} sx={{ pl: 4, pr: 1, pb: 1, m: 0, listStyle: 'none' }}>
                      {steps.map((m, n) => (
                        <Stack
                          key={n}
                          component="li"
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: 'flex-start' }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ minWidth: 18, lineHeight: '20px' }}
                          >
                            {n + 1}.
                          </Typography>
                          {/* The same shape the exported PDF draws. */}
                          <TurnIcon
                            type={m.type}
                            sx={{ fontSize: 17, color: 'primary.main', flexShrink: 0 }}
                          />
                          <Typography variant="caption" sx={{ flex: 1, lineHeight: '20px' }}>
                            {m.instruction}
                          </Typography>
                          {m.distanceMeters > 0 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ lineHeight: '20px', whiteSpace: 'nowrap' }}
                            >
                              {turnDistance(m.distanceMeters, city.units)}
                            </Typography>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  );
                })()}

                {/* Transit legs list their actual services, which is the only
                    way a traveller knows which train to board. */}
                {leg?.transitDetail && leg.transitDetail.length > 0 && (
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', pl: 4, pb: 0.75 }} useFlexGap>
                    {leg.transitDetail
                      .filter((d) => d.mode !== 'WALK')
                      .map((d, i) => (
                        <Chip
                          key={i}
                          size="small"
                          variant="outlined"
                          label={`${d.routeName ?? d.mode}${d.headsign ? ` → ${d.headsign}` : ''}`}
                        />
                      ))}
                  </Stack>
                )}

                <Stack
                  direction="row"
                  spacing={1.5}
                  ref={(node: HTMLDivElement | null) => { rowRefs.current[index] = node; }}
                  onMouseEnter={() => onStopHover?.(index)}
                  onMouseLeave={() => onStopHover?.(null)}
                  onClick={() => onStopFocus?.(index)}
                  draggable={movable(index)}
                  onDragStart={() => movable(index) && setDragFrom(index)}
                  onDragOver={(event) => {
                    if (dragFrom === null || !movable(index)) return;
                    // Without this the drop is refused by the browser.
                    event.preventDefault();
                    setDragOver(index);
                  }}
                  onDrop={() => {
                    if (dragFrom !== null && movable(index) && dragFrom !== index) {
                      onReorder?.(dragFrom, index);
                    }
                    setDragFrom(null);
                    setDragOver(null);
                  }}
                  onDragEnd={() => { setDragFrom(null); setDragOver(null); }}
                  sx={{
                    alignItems: 'flex-start',
                    p: 1, borderRadius: 1.5,
                    // Grab wins where the row can be dragged; a row that only
                    // takes you to the stop on the map still says it is live.
                    cursor: movable(index) ? 'grab' : onStopFocus ? 'pointer' : 'default',
                    bgcolor: highlighted ? 'action.hover' : 'transparent',
                    // Dimmed once taken off the request, so a stop that is
                    // still drawn no longer reads as one that is still asked
                    // for. Struck through on the name itself, below.
                    opacity: dragFrom === index || pendingRemoval.includes(index) ? 0.4 : 1,
                    // Shows where the stop would land.
                    borderTop: dragOver === index && dragFrom !== index
                      ? '2px solid' : '2px solid transparent',
                    borderTopColor: dragOver === index && dragFrom !== index
                      ? 'primary.main' : 'transparent',
                    transition: 'background-color .15s, opacity .15s',
                  }}
                >
                  {movable(index) && (
                    <DragIndicatorIcon
                      fontSize="small"
                      sx={{ color: 'text.disabled', mt: 0.25, ml: -0.75, flexShrink: 0 }}
                    />
                  )}
                  {/*
                    A place gone past is a via point, not a stop, and the
                    badge says so in the shape the map uses: hollow, turned
                    on its corner, with the number upright inside it.
                  */}
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      flexShrink: 0,
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      ...(passingBy(stop)
                        ? {
                          transform: 'rotate(45deg)',
                          border: `2.5px solid ${STOP_COLOURS.visit}`,
                          color: STOP_COLOURS.visit,
                        }
                        : {
                          borderRadius: '50%',
                          bgcolor: STOP_COLOURS[stop.kind],
                          color: '#fff',
                        }),
                    }}
                  >
                    <Box
                      component="span"
                      sx={passingBy(stop)
                        ? { transform: 'rotate(-45deg)', fontSize: 11 }
                        : undefined}
                    >
                      {stop.kind === 'start' ? 'S' : stop.kind === 'end' ? 'E' : number}
                    </Box>
                  </Box>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
                      {/*
                        A dropped pin has no place record behind it, so there
                        is nothing to look up — but "38.92708, -77.01883" sat
                        in the round as dead text while every neighbour opened,
                        and a stop somebody placed by hand is exactly the one
                        they are least sure about. It opens what IS known: what
                        is at the coordinate, and when the round is there.
                      */}
                      {stop.poiId && onShowDetails ? (
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          variant="body2"
                          sx={{ fontWeight: 600, textAlign: 'left', p: 0 }}
                          onMouseDown={(event) => event.stopPropagation()}
                          onDragStart={(event) => { event.preventDefault(); event.stopPropagation(); }}
                          onClick={() => onShowDetails(stop.poiId!)}
                        >
                          {stop.name}
                        </Link>
                      ) : !stop.poiId && onShowPoint && stop.kind !== 'start' && stop.kind !== 'end' ? (
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          variant="body2"
                          sx={{ fontWeight: 600, textAlign: 'left', p: 0 }}
                          onMouseDown={(event) => event.stopPropagation()}
                          onDragStart={(event) => { event.preventDefault(); event.stopPropagation(); }}
                          onClick={() => onShowPoint(index)}
                        >
                          {stop.name}
                        </Link>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            textDecoration: pendingRemoval.includes(index) ? 'line-through' : 'none',
                          }}
                        >
                          {stop.name}
                        </Typography>
                      )}
                      {stop.kind === 'lunch' && <Chip size="small" label="Lunch" color="warning" />}
                      {stop.kind === 'coffee' && <Chip size="small" label="Coffee" />}
                      {pendingRemoval.includes(index) && (
                        <Chip size="small" variant="outlined" label="Removed — re-planning" />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {stop.kind === 'start' && `Leave ${formatClock(stop.departureTime)}`}
                      {stop.kind === 'end' && `Arrive ${formatClock(stop.arrivalTime)}`}
                      {stop.kind !== 'start' && stop.kind !== 'end' && (
                        `Arrive ${formatClock(stop.arrivalTime)}`
                        /* No stay is a pass, not a stop of zero minutes. */
                        + (stop.dwellMinutes > 0
                          ? ` · stay ${formatDuration(stop.dwellMinutes)}`
                            + ` · leave ${formatClock(stop.departureTime)}`
                          : ' · passes by, no stop')
                      )}
                    </Typography>
                  </Box>

                  {/*
                    Everything but the start and end can be lingered at. The
                    schedule below re-times itself from this, and a long
                    enough stay can push a later stop out of the day — which
                    the plan then says outright rather than overrunning.
                  */}
                  {onDwellChange && stop.kind !== 'start' && stop.kind !== 'end' && (
                    <TextField
                      size="small"
                      type="number"
                      label="Stay"
                      /*
                       * The row itself is draggable for reordering, so a drag
                       * starting inside the field would move the stop instead
                       * of selecting the number. Cancelled at source.
                       */
                      onDragStart={(event) => { event.preventDefault(); event.stopPropagation(); }}
                      onMouseDown={(event) => event.stopPropagation()}
                      defaultValue={stop.dwellMinutes}
                      // Keyed by value so a re-plan that changes the stay is
                      // reflected, without fighting the field while typing.
                      key={`${stop.poiId ?? index}-${stop.dwellMinutes}`}
                      onBlur={(event) => {
                        const next = Number(event.target.value);
                        if (Number.isFinite(next) && next >= 0 && next !== stop.dwellMinutes) {
                          onDwellChange(index, Math.min(600, Math.round(next)));
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
                      }}
                      slotProps={{
                        htmlInput: { min: 0, max: 600, step: 5, 'aria-label': 'Minutes at this stop' },
                        input: { endAdornment: <InputAdornment position="end">min</InputAdornment> },
                      }}
                      sx={{ width: 104, flexShrink: 0 }}
                    />
                  )}

                  {/* Removing here saves going back to the form to find the
                      place and take it out of a list. */}
                  {onRemoveStop && stop.kind !== 'start' && stop.kind !== 'end' && (
                    <Tooltip title="Remove this stop" arrow>
                      <IconButton
                        size="small"
                        aria-label={`Remove ${stop.name}`}
                        /*
                          The click stops here.

                          The row around this opens the stop's details, so
                          removing one both took it off the round AND opened
                          an information panel about the thing just removed.
                          `onMouseDown` was already held back for the drag
                          handle's sake; the click itself was still bubbling.
                        */
                        onClick={(event) => {
                          event.stopPropagation();
                          onRemoveStop(index);
                        }}
                        onMouseDown={(event) => event.stopPropagation()}
                        onDragStart={(event) => { event.preventDefault(); event.stopPropagation(); }}
                        sx={{ flexShrink: 0, mt: 0.25, color: 'text.disabled' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* ── infrastructure proximity ─────────────────────────────── */}
      {/*
        Set apart from the panels around it, because it is a different kind of
        thing from all of them.

        Everything else on this screen is something you SET; this is something
        that was MEASURED. They were drawn identically — the same outlined
        card, the same accent — beside a form whose "Near help" slider is
        about hospitals, police and fire stations too, so two unrelated
        numbers about the same subject looked like one another's readout. They
        share nothing: this is composed from the city's own weighting, while
        Near help ranks one way round against another and never moves this
        figure.

        A filled panel rather than an outlined one, and the score in the plain
        text colour rather than the accent that marks the things you can
        change.
      */}
      {ips && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
            <ShieldOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h3">{ips.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                Measured, not set
              </Typography>
            </Box>
            <Typography variant="h2" color="text.primary">{ips.score}</Typography>
            <Typography variant="body2" color="text.secondary">/ 100</Typography>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={ips.score}
            color="inherit"
            sx={{ height: 6, borderRadius: 3, mb: 1.5, color: 'text.secondary' }}
          />

          {/*
            Every figure the score is built from, because a score nobody can
            check is a score nobody should trust. The radius is what was asked
            for, "within" counts distinct facilities near any stop, "furthest"
            is the stop that fared least well, and the figure on the right is
            that category's mean across all of them.
          */}
          <Stack spacing={0.75} sx={{ mb: 1.5 }}>
            {ips.breakdown.map((item) => (
              <Box key={item.categoryKey}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                  <Typography variant="caption" sx={{ flex: 1, fontWeight: 600 }}>
                    {item.label}
                  </Typography>
                  {/*
                    "Share of score", not "weight". The word weight is spoken
                    for: the form beside this has ranking weights, one of them
                    named for these very categories, and a column of
                    percentages called weight invited the reading that this
                    panel was showing what had been set there. It is not —
                    this is how much each kind of place counts towards the
                    number above, which comes from the city's own figures.
                  */}
                  <Typography variant="caption" color="text.secondary">
                    {(item.effectiveWeight * 100).toFixed(0)}% of score
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, minWidth: 34, textAlign: 'right' }}
                  >
                    {item.categoryScore.toFixed(2)}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {/*
                    Whole metres. These arrive as raw floats — a furthest stop
                    read "2163.88668891 m", which claims the distance to a fire
                    station is known to a tenth of a micron. Nothing downstream
                    uses the decimals and nobody can act on them.
                  */}
                  within {Math.round(item.radiusM)} m: {item.facilitiesWithinRadius}
                  {item.worstWaypointDistanceM !== null
                    ? ` · furthest stop ${Math.round(item.worstWaypointDistanceM)} m`
                    : ' · none in this city'}
                </Typography>
              </Box>
            ))}
          </Stack>

          {/*
            Shown every time, deliberately. The measure is easy to mistake for
            a safety rating, and these are the assumptions that would
            otherwise be made silently.
          */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {ips.disclaimer} Measured at the {ips.waypointCount} stops only — the
            roads between them are not assessed, so two itineraries with the same
            stops score the same. A high score means facilities are nearby, not
            that the route is safe. It is not the “Near help” setting in the
            form: that one chooses between ways round, and changes nothing
            here.
          </Typography>
        </Paper>
      )}

      {/* ── avoided areas ────────────────────────────────────────── */}
      {/*
        Shown only when something could NOT be kept clear of.

        Avoidance that worked needs no panel: the operator asked for it, the
        map draws the zones, and "Keeping clear of 30 areas, 500 m from each"
        is a receipt for an instruction nobody doubted. Before that it was
        thirty chips saying the same thing.

        A zone the route passes through anyway is the opposite — the one fact
        here that contradicts what was asked for, and the only one worth
        taking screen space for.
      */}
      {violatedZones.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderColor: AVOID_VIOLATED_COLOUR }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
            <BlockIcon fontSize="small" sx={{ color: AVOID_VIOLATED_COLOUR }} />
            <Typography variant="h3">
              {violatedZones.length === 1
                ? 'One area could not be avoided'
                : `${violatedZones.length} areas could not be avoided`}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            The route passes through {violatedZones.length === 1 ? 'it' : 'these'} — there is
            no way round without {violatedZones.length === 1 ? 'it' : 'them'}.
          </Typography>
          <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {violatedZones.map((zone) => (
              <Chip
                key={zone.poiId}
                size="small"
                label={`${zone.name} · ${zone.radiusM} m`}
                variant="outlined"
                sx={{ borderColor: AVOID_VIOLATED_COLOUR, color: AVOID_VIOLATED_COLOUR }}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/*
        What did not fit is not reported.

        It was a count at the foot of the itinerary — "21 more places were
        considered and left out for want of time" — which is a fact about the
        planner's search rather than about the round that came back. Nobody
        reading a finished schedule is owed the list of things it is not, and
        the number only invited the question "which ones?", which the line
        could not answer.

        `plan.dropped` is still on the response and still read elsewhere; only
        this line has gone.
      */}
    </Stack>
  );

  return (
    <Stack spacing={2}>
      <Tabs
        value={view}
        onChange={(_, next: 'schedule' | 'coverage') => setView(next)}
        variant="fullWidth"
        sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
      >
        <Tab value="schedule" label="Mission schedule" />
        <Tab value="coverage" label="Coverage" />
      </Tabs>

      {/*
        Neither side is thrown away once it has been looked at.

        Both are hidden rather than unmounted: the schedule would otherwise
        lose the turn-by-turn somebody fetched and the row they had scrolled
        to, and the analysis would re-ask the server, empty its filters and
        close whatever was open every time the tab was touched — which read
        as the whole panel reloading on a click.

        The analysis is still not built until it is first opened, so a round
        nobody analyses costs nothing.
      */}
      <Box sx={{ display: view === 'schedule' ? 'block' : 'none' }}>{schedule}</Box>
      <Box sx={{ display: view === 'coverage' ? 'block' : 'none' }}>
      {coverageOpened.current && (
        <CoveragePanel
          plan={plan}
          city={city}
          {...(basemapUrl !== undefined ? { basemapUrl } : {})}
          {...(coverageIgnoreId ? { ignoreSourceId: coverageIgnoreId } : {})}
          {...(coverageDayNote ? { dayNote: coverageDayNote } : {})}
          {...(coverageRoundDate ? { roundDate: coverageRoundDate } : {})}
          {...(onShowDetails ? { onShowDetails } : {})}
          {...(onStopFocus ? { onStopFocus } : {})}
          {...(onStopReveal ? { onStopReveal } : {})}
          {...(onStopHover ? { onStopHover } : {})}
        />
      )}
      </Box>
    </Stack>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="body1" sx={{ fontWeight: 620 }}>{value}</Typography>
    </Box>
  );
}
